import { useState } from "react";
import { ArrowUpRight, Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatArtValue, type ArtValue } from "../../../lib/schedule-art/format-art-value";
import { metadataDefinitions } from "../../../lib/schedule-presets/metadata-schema";
import { useMetadataEditorNavigationStore } from "../../../stores/metadata-editor-navigation-store";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { useMetadataForm } from "./MetadataFormContext";

function formatSample(binding?: string): ArtValue {
  if (binding?.endsWith(".time")) return "18:30";
  const start = new Date(2026, 9, 5);
  const end = new Date(2026, 9, 11);
  if (binding === "week.range") return [start, end];
  if (binding === "week.end_date") return end;
  return start;
}

type Props = {
  value: unknown;
  binding?: string;
  optional: boolean;
  disabled?: boolean;
  onChange: (value: string | undefined) => void;
};

export function MetadataFormatPicker({ value, binding, optional, disabled, onChange }: Props) {
  const { t } = useTranslation();
  const { values } = useMetadataForm();
  const [open, setOpen] = useState(false);
  const openFormat = useMetadataEditorNavigationStore(state => state.openFormat);
  const definitions = metadataDefinitions(values, "formats");
  const sample = formatSample(binding);
  const defaultOutput = formatArtValue(sample);
  const definitionEntries = Object.entries(definitions);
  const validDefinitions = definitionEntries.filter((entry): entry is [string, string] => typeof entry[1] === "string");
  const choices = validDefinitions.map(([name, pattern]) => {
    const output = formatArtValue(sample, pattern);
    return { name, pattern, output };
  });
  const missing = value !== undefined && !choices.some(choice => choice.name === value);

  const select = (name: string | undefined) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white">
      <CollapsibleTrigger asChild>
        <button type="button" className="group flex min-h-10 w-full items-center gap-2 px-3 py-1 text-left">
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <p className="text-xs font-medium">{t("metadata.format.bindDefined")}</p>
            <p className={`min-w-0 truncate text-right text-xs ${missing ? "text-destructive" : "text-muted-foreground"}`}>
              {value === undefined ? t("metadata.format.unbound") : String(value)}
            </p>
          </div>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 border-t border-neutral-200 p-2">
        <p className="text-xs text-muted-foreground">{t("metadata.format.sample", { value: defaultOutput })}</p>
        {missing && (
          <p className="break-words text-xs text-destructive">
            {t("metadata.format.missing", { name: String(value) })}
          </p>
        )}
        {optional && (
          <button type="button" disabled={disabled} onClick={() => select(undefined)}
            className="flex w-full items-center justify-between gap-2 rounded-md border border-neutral-200 px-3 py-2 text-left text-xs hover:bg-neutral-50 disabled:opacity-50">
            {t("metadata.format.unbound")}
            {value === undefined && <Check className="size-4 shrink-0 text-primary" />}
          </button>
        )}
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {choices.map(choice => (
            <FormatChoice key={choice.name} selected={value === choice.name} disabled={disabled}
              output={choice.output} name={choice.name} detail={choice.pattern}
              onClick={() => select(choice.name)} onOpen={() => openFormat(choice.name)} />
          ))}
        </div>
        {choices.length === 0 && <p className="text-xs text-muted-foreground">{t("metadata.format.empty")}</p>}
      </CollapsibleContent>
    </Collapsible>
  );
}

function FormatChoice({ selected, disabled, output, name, detail, onClick, onOpen }: {
  selected: boolean;
  disabled?: boolean;
  output: string;
  name: string;
  detail: string;
  onClick: () => void;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const colors = selected ? "border-primary bg-primary/5" : "border-neutral-200 bg-background";
  return (
    <div className={`grid grid-cols-2 overflow-hidden rounded-md border ${colors}`}>
      <button type="button" disabled={disabled} onClick={onClick}
        className="min-w-0 space-y-2 p-3 text-left hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50">
        <span className="flex items-center justify-between gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("metadata.format.preview")}
          {selected && <Check className="size-3.5 shrink-0 text-primary" />}
        </span>
        <span className="block break-words text-base font-semibold tabular-nums">{output || t("metadata.format.blank")}</span>
        <span className="block text-[10px] text-muted-foreground">{selected ? t("metadata.format.bound") : t("metadata.format.select")}</span>
      </button>
      <button type="button" onClick={onOpen}
        className="min-w-0 space-y-2 border-l border-neutral-200 bg-neutral-50/70 p-3 text-left hover:bg-neutral-100">
        <span className="flex items-center justify-between gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("metadata.format.variable")}
          <ArrowUpRight className="size-3.5 shrink-0" />
        </span>
        <span className="block break-all font-mono text-xs font-medium text-primary underline decoration-primary/30 underline-offset-4">{name}</span>
        <span className="block break-all font-mono text-[10px] text-muted-foreground">{detail}</span>
        <span className="block text-[10px] text-muted-foreground">{t("metadata.format.edit")}</span>
      </button>
    </div>
  );
}
